/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { authService } from '../services/auth.service';
import { isPublicPath } from '../services/api';
import { getNotificacoesStreamURL } from '../utils/apiConfig';
import { limparRascunhoAgendamento } from '../utils/agendamentoStorage';

export interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
  solicitante_id: number;
  role?: string;
  group_id?: number;
}

interface EmpresaContextType {
  empresa: Empresa | null;
  empresas: Empresa[];
  setEmpresa: (empresa: Empresa | null) => void;
  loading: boolean;
  userRole: string;
  isAdmin: boolean;
  isIntegracoes: boolean;
}

const EmpresaContext = createContext<EmpresaContextType | undefined>(undefined);

function getInitialEmpresas(): Empresa[] {
  try {
    const saved = localStorage.getItem('customer_empresas');
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore
  }
  return [];
}

function getInitialEmpresa(initialEmpresas: Empresa[]): Empresa | null {
  try {
    const savedId = localStorage.getItem('customer_empresa_id');
    if (savedId && initialEmpresas.length > 0) {
      const id = Number(savedId);
      const found = initialEmpresas.find((e) => e.id === id);
      if (found) return found;
    }
    return initialEmpresas[0] || null;
  } catch {
    // ignore
  }
  return null;
}

export function EmpresaProvider({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();

  const [empresas, setEmpresas] = useState<Empresa[]>(getInitialEmpresas);
  const [empresa, setEmpresaState] = useState<Empresa | null>(() => getInitialEmpresa(getInitialEmpresas()));
  const [loading, setLoading] = useState(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveErrorsRef = useRef(0);

  const refreshEmpresas = useCallback(async () => {
    if (!user) return;
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (isPublicPath(currentPath)) return;

    try {
      const freshEmpresas = await authService.getEmpresas();
      if (Array.isArray(freshEmpresas) && freshEmpresas.length > 0) {
        setEmpresas(freshEmpresas);
        localStorage.setItem('customer_empresas', JSON.stringify(freshEmpresas));

        const currentSavedId = localStorage.getItem('customer_empresa_id');
        if (currentSavedId) {
          const empId = Number(currentSavedId);
          const found = freshEmpresas.find((e: Empresa) => e.id === empId);
          if (found) {
            setEmpresaState(found);
          } else {
            setEmpresaState(freshEmpresas[0]);
            localStorage.setItem('customer_empresa_id', freshEmpresas[0].id.toString());
            limparRascunhoAgendamento();
            queryClient.invalidateQueries();
          }
        } else {
          setEmpresaState(freshEmpresas[0]);
          localStorage.setItem('customer_empresa_id', freshEmpresas[0].id.toString());
        }
      } else if (Array.isArray(freshEmpresas) && freshEmpresas.length === 0) {
        setEmpresas([]);
        setEmpresaState(null);
        localStorage.removeItem('customer_empresas');
        localStorage.removeItem('customer_empresa_id');
        logout();
      }
    } catch (err) {
      console.warn('Erro ao atualizar lista de empresas da API:', err);
    } finally {
      setLoading(false);
    }
  }, [user, queryClient, logout]);

  useEffect(() => {
    if (!user) return;
    refreshEmpresas();
  }, [user, refreshEmpresas]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;
    const currentPath = window.location.pathname;
    if (isPublicPath(currentPath)) return;

    const handleRefreshEvent = () => {
      refreshEmpresas();
    };

    window.addEventListener('REFRESH_CLIENT_EMPRESAS', handleRefreshEvent);
    window.addEventListener('focus', handleRefreshEvent);

    let eventSource: EventSource | null = null;
    let isUnmounted = false;

    const connectSSE = () => {
      if (isUnmounted) return;
      const token = localStorage.getItem('customer_token');
      if (!token) return;

      const sseUrl = getNotificacoesStreamURL(token);
      try {
        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          consecutiveErrorsRef.current = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as Record<string, unknown>;
            if (data?.type === 'heartbeat') return;

            if (data?.type === 'FORCE_LOGOUT') {
              console.warn('⚠️ [Customer SSE] FORCE_LOGOUT recebido. Encerrando sessão imediatamente...');
              if (eventSource) {
                eventSource.close();
                eventSource = null;
              }
              if (data.message && typeof data.message === 'string') {
                sessionStorage.setItem('logout_reason_message', data.message);
              }
              logout();
              window.location.replace('/login');
              return;
            }

            if (data?.type === 'REFRESH_CLIENT_EMPRESAS') {
              console.log('🔄 [Customer SSE] REFRESH_CLIENT_EMPRESAS recebido. Atualizando empresas...');
              refreshEmpresas();
              return;
            }
          } catch {
            // ignore parse errors
          }
        };

        eventSource.onerror = () => {
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          if (isUnmounted) return;
          consecutiveErrorsRef.current += 1;
          const delay = Math.min(30000, 2000 * Math.pow(1.5, consecutiveErrorsRef.current - 1));
          reconnectTimeoutRef.current = setTimeout(connectSSE, delay);
        };
      } catch {
        // ignore connection errors
      }
    };

    connectSSE();

    return () => {
      isUnmounted = true;
      window.removeEventListener('REFRESH_CLIENT_EMPRESAS', handleRefreshEvent);
      window.removeEventListener('focus', handleRefreshEvent);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };
  }, [user, refreshEmpresas, logout]);

  const setEmpresa = (newEmpresa: Empresa | null) => {
    const empresaAnterior = empresa;
    setEmpresaState(newEmpresa);
    if (newEmpresa) {
      localStorage.setItem('customer_empresa_id', newEmpresa.id.toString());

      // Invalidar todas as queries e resetar fluxo de agendamento ao trocar empresa
      if (!empresaAnterior || empresaAnterior.id !== newEmpresa.id) {
        limparRascunhoAgendamento();
        queryClient.invalidateQueries();

        // Se estiver em qualquer tela do fluxo de agendamento, redireciona imediatamente para a tela de início
        if (
          typeof window !== 'undefined' &&
          (window.location.pathname.startsWith('/agendamento') ||
            window.location.pathname.startsWith('/exames/agendamento'))
        ) {
          console.log(
            '🔄 [EmpresaContext] Empresa alterada durante agendamento. Limpando rascunho e redirecionando para a tela inicial.'
          );
          window.location.href = '/';
        }
      }
    } else {
      localStorage.removeItem('customer_empresa_id');
      limparRascunhoAgendamento();
      queryClient.invalidateQueries();
      if (
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/agendamento') ||
          window.location.pathname.startsWith('/exames/agendamento'))
      ) {
        window.location.href = '/';
      }
    }
  };

  const rawRole = (empresa?.role || user?.role || 'ADMIN').toUpperCase();
  const rawGroupId = empresa?.group_id
    ? Number(empresa.group_id)
    : user?.group_id
    ? Number(user.group_id)
    : undefined;

  const isIntegracoes =
    rawRole === 'INTEGRACOES' ||
    rawRole === 'INTEGRACAO' ||
    rawRole === 'CLIENTE_INTEGRACOES' ||
    rawRole === 'CLIENTE-INTEGRACOES' ||
    rawGroupId === 12;

  const userRole = isIntegracoes ? 'INTEGRACOES' : rawRole;
  const isAdmin = !isIntegracoes && (userRole === 'ADMIN' || userRole === 'ADMINISTRADOR' || userRole === 'CLIENTE_ADMIN');

  return (
    <EmpresaContext.Provider
      value={{
        empresa,
        empresas,
        setEmpresa,
        loading,
        userRole,
        isAdmin,
        isIntegracoes,
      }}
    >
      {children}
    </EmpresaContext.Provider>
  );
}

export function useEmpresa() {
  const context = useContext(EmpresaContext);
  if (context === undefined) {
    throw new Error('useEmpresa must be used within an EmpresaProvider');
  }
  return context;
}

